/**
 * Email renderer — compiles a block-based document into email-safe HTML.
 *
 * Output is deliberately table-based with inline styles. Gmail, Outlook
 * desktop, and older clients strip <style> tags and ignore flexbox/grid,
 * so we stay with <table> + inline CSS — the 2000s-era approach that
 * still renders everywhere.
 */

import type { Block, TemplateTheme, Subscriber } from '@/types/newsletter';
import { mergeTagsInString, DEFAULT_THEME } from '@/types/newsletter';

type RenderContext = {
  theme: TemplateTheme;
  /** Subscriber data for merge tags. Optional — for preview without real data. */
  subscriber?: Pick<Subscriber, 'firstName' | 'lastName' | 'email' | 'company'>;
  /** Unsubscribe URL (appended via footer block automatically) */
  unsubscribeUrl?: string;
  /** Campaign ID for click/open tracking */
  campaignId?: string;
  sendId?: string;
  /** Base URL used for tracking pixel + click-redirect */
  trackingBaseUrl?: string;
  /** When true, skip tracking (used in previews/tests) */
  skipTracking?: boolean;
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function merge(str: string, ctx: RenderContext): string {
  if (!ctx.subscriber) return str;
  return mergeTagsInString(str, ctx.subscriber);
}

/** Wrap a URL with the click-tracking redirect when tracking is enabled. */
function trackUrl(url: string, ctx: RenderContext): string {
  if (ctx.skipTracking || !ctx.trackingBaseUrl || !ctx.campaignId || !ctx.sendId) return url;
  const params = new URLSearchParams({ c: ctx.campaignId, s: ctx.sendId, u: url });
  return `${ctx.trackingBaseUrl}/c?${params.toString()}`;
}

// ───────────── Block renderers ─────────────

function renderHeading(block: Block, ctx: RenderContext): string {
  const text = merge(String(block.content.text ?? ''), ctx);
  const level = Number(block.content.level ?? 2);
  const align = String(block.content.align ?? 'left');
  const size =
    level === 1 ? '32px' :
    level === 2 ? '26px' :
    level === 3 ? '20px' : '16px';
  return `
    <tr><td style="padding:16px 24px 8px 24px;" align="${align}">
      <h${level} style="
        margin:0;
        color:${ctx.theme.textColor};
        font-family:${ctx.theme.headingFont};
        font-size:${size};
        line-height:1.25;
        font-weight:600;
        text-align:${align};
      ">${escapeHtml(text)}</h${level}>
    </td></tr>`;
}

function renderText(block: Block, ctx: RenderContext): string {
  const html = merge(String(block.content.html ?? block.content.text ?? ''), ctx);
  const align = String(block.content.align ?? 'left');
  const fontSize = String(block.content.fontSize ?? '15px');
  // Allow minimal safe HTML; escape if plain text provided
  const content = block.content.html ? html : escapeHtml(html).replace(/\n/g, '<br/>');
  return `
    <tr><td style="padding:8px 24px;" align="${align}">
      <div style="
        color:${ctx.theme.textColor};
        font-family:${ctx.theme.bodyFont};
        font-size:${fontSize};
        line-height:1.6;
        text-align:${align};
      ">${content}</div>
    </td></tr>`;
}

function renderButton(block: Block, ctx: RenderContext): string {
  const text = merge(String(block.content.text ?? 'Click here'), ctx);
  const rawUrl = merge(String(block.content.url ?? '#'), ctx);
  const url = trackUrl(rawUrl, ctx);
  const align = String(block.content.align ?? 'center');
  const color = String(block.content.color ?? ctx.theme.primaryColor);
  const textColor = String(block.content.textColor ?? '#ffffff');
  return `
    <tr><td style="padding:16px 24px;" align="${align}">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:0 auto;">
        <tr><td style="
          background:${color};
          border-radius:3px;
          padding:12px 28px;
          text-align:center;
        ">
          <a href="${escapeHtml(url)}" target="_blank" style="
            color:${textColor};
            font-family:${ctx.theme.bodyFont};
            font-size:14px;
            font-weight:600;
            text-decoration:none;
            letter-spacing:0.05em;
            text-transform:uppercase;
          ">${escapeHtml(text)}</a>
        </td></tr>
      </table>
    </td></tr>`;
}

function renderImage(block: Block, ctx: RenderContext): string {
  const url = String(block.content.url ?? '');
  const alt = String(block.content.alt ?? '');
  const link = String(block.content.link ?? '');
  const align = String(block.content.align ?? 'center');
  const width = String(block.content.width ?? '100%');
  if (!url) return '';
  const img = `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt)}" width="${escapeHtml(width)}" style="
    display:block;
    max-width:100%;
    height:auto;
    border:0;
    outline:none;
    text-decoration:none;
    margin:0 auto;
  "/>`;
  const wrapped = link
    ? `<a href="${escapeHtml(trackUrl(link, ctx))}" target="_blank" style="text-decoration:none;">${img}</a>`
    : img;
  return `
    <tr><td style="padding:16px 24px;" align="${align}">
      ${wrapped}
    </td></tr>`;
}

function renderDivider(block: Block, ctx: RenderContext): string {
  const color = String(block.content.color ?? '#e9e6df');
  return `
    <tr><td style="padding:8px 24px;">
      <hr style="
        border:0;
        border-top:1px solid ${color};
        margin:0;
      "/>
    </td></tr>`;
  void ctx;
}

function renderSpacer(block: Block): string {
  const height = Number(block.content.height ?? 24);
  return `
    <tr><td style="padding:0;line-height:${height}px;height:${height}px;font-size:${height}px;">
      &nbsp;
    </td></tr>`;
}

function renderColumns(block: Block, ctx: RenderContext): string {
  const cols = (block.content.columns as Block[][] | undefined) ?? [];
  const colWidth = Math.floor(100 / Math.max(cols.length, 1));
  const cells = cols.map((colBlocks) => {
    const inner = colBlocks.map((b) => renderBlock(b, ctx)).join('');
    return `<td valign="top" width="${colWidth}%" style="padding:0 4px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">${inner}</table>
    </td>`;
  }).join('');
  return `
    <tr><td style="padding:8px 20px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>${cells}</tr>
      </table>
    </td></tr>`;
}

function renderSocial(block: Block, ctx: RenderContext): string {
  const links = (block.content.links as Array<{ network: string; url: string }> | undefined) ?? [];
  if (links.length === 0) return '';
  const items = links.map((l) => `
    <td style="padding:0 6px;">
      <a href="${escapeHtml(trackUrl(l.url, ctx))}" target="_blank" style="
        color:${ctx.theme.primaryColor};
        font-family:${ctx.theme.bodyFont};
        font-size:12px;
        text-decoration:none;
        text-transform:uppercase;
        letter-spacing:0.1em;
      ">${escapeHtml(l.network)}</a>
    </td>`).join('');
  return `
    <tr><td style="padding:16px 24px;" align="center">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin:0 auto;">
        <tr>${items}</tr>
      </table>
    </td></tr>`;
}

function renderFooter(block: Block, ctx: RenderContext): string {
  const companyName = String(block.content.companyName ?? 'Talbert Architectural Panels & Doors, Inc.');
  const address = String(block.content.address ?? 'City of Industry, CA');
  const fscLine = String(block.content.fscLine ?? 'FSC® C014235');
  const extraText = merge(String(block.content.text ?? ''), ctx);
  const unsubUrl = ctx.unsubscribeUrl ?? '#';
  return `
    <tr><td style="
      padding:32px 24px 24px 24px;
      border-top:1px solid #d3ccbf;
      font-family:${ctx.theme.bodyFont};
      font-size:11px;
      line-height:1.6;
      color:#8f826f;
      text-align:center;
    ">
      ${extraText ? `<div style="margin-bottom:12px;">${escapeHtml(extraText)}</div>` : ''}
      <div style="font-weight:600;color:#6f6350;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:4px;">
        ${escapeHtml(companyName)}
      </div>
      <div>${escapeHtml(address)}</div>
      <div style="margin-top:4px;font-size:10px;letter-spacing:0.1em;">${escapeHtml(fscLine)}</div>
      <div style="margin-top:16px;">
        <a href="${escapeHtml(unsubUrl)}" style="color:#8f826f;text-decoration:underline;">
          Unsubscribe
        </a>
        &nbsp;·&nbsp;
        <a href="${escapeHtml(unsubUrl)}&action=preferences" style="color:#8f826f;text-decoration:underline;">
          Update Preferences
        </a>
      </div>
    </td></tr>`;
}

function renderBlock(block: Block, ctx: RenderContext): string {
  switch (block.type) {
    case 'heading':  return renderHeading(block, ctx);
    case 'text':     return renderText(block, ctx);
    case 'button':   return renderButton(block, ctx);
    case 'image':    return renderImage(block, ctx);
    case 'divider':  return renderDivider(block, ctx);
    case 'spacer':   return renderSpacer(block);
    case 'columns':  return renderColumns(block, ctx);
    case 'social':   return renderSocial(block, ctx);
    case 'footer':   return renderFooter(block, ctx);
    default:         return '';
  }
}

// ───────────── Main export ─────────────

export function renderEmailHtml(
  blocks: Block[],
  themeOverride?: Partial<TemplateTheme>,
  ctxOverrides?: Omit<RenderContext, 'theme'>,
): string {
  const theme = { ...DEFAULT_THEME, ...themeOverride };
  const ctx: RenderContext = { theme, ...ctxOverrides };

  const hasFooter = blocks.some((b) => b.type === 'footer');
  const allBlocks: Block[] = hasFooter
    ? blocks
    : [...blocks, { id: '__footer__', type: 'footer', content: {} }];

  const body = allBlocks.map((b) => renderBlock(b, ctx)).join('');

  // Tracking pixel — 1x1 transparent image that logs an open event
  const openPixel = ctx.skipTracking || !ctx.trackingBaseUrl || !ctx.campaignId || !ctx.sendId
    ? ''
    : `<img src="${ctx.trackingBaseUrl}/o?c=${ctx.campaignId}&s=${ctx.sendId}" width="1" height="1" style="display:block;width:1px;height:1px;" alt=""/>`;

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <meta name="x-apple-disable-message-reformatting"/>
  <title></title>
  <!--[if mso]><style type="text/css">table,td{border-collapse:collapse;mso-line-height-rule:exactly;mso-table-lspace:0;mso-table-rspace:0;}</style><![endif]-->
</head>
<body style="margin:0;padding:0;background:${theme.backgroundColor};font-family:${theme.bodyFont};">
  <div style="display:none;font-size:1px;color:${theme.backgroundColor};line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;">
    <!-- Preheader placeholder — overridden per campaign -->
  </div>
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" bgcolor="${theme.backgroundColor}">
    <tr><td align="center" style="padding:32px 16px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="${theme.containerWidth}" style="max-width:${theme.containerWidth}px;width:100%;background:#ffffff;border:1px solid #e9e6df;">
        ${body}
      </table>
      ${openPixel}
    </td></tr>
  </table>
</body>
</html>`;
}

export function renderEmailText(blocks: Block[], ctx?: { subscriber?: RenderContext['subscriber'] }): string {
  const chunks: string[] = [];
  for (const b of blocks) {
    const renderCtx: RenderContext = { theme: DEFAULT_THEME, subscriber: ctx?.subscriber };
    switch (b.type) {
      case 'heading':
        chunks.push(merge(String(b.content.text ?? ''), renderCtx).toUpperCase());
        chunks.push('');
        break;
      case 'text':
        chunks.push(merge(String(b.content.html ?? b.content.text ?? ''), renderCtx).replace(/<[^>]+>/g, ''));
        chunks.push('');
        break;
      case 'button':
        chunks.push(`→ ${merge(String(b.content.text ?? ''), renderCtx)}: ${String(b.content.url ?? '')}`);
        chunks.push('');
        break;
      case 'divider':
        chunks.push('— — —');
        chunks.push('');
        break;
      case 'footer':
        chunks.push('---');
        chunks.push(String(b.content.companyName ?? 'Talbert Architectural Panels & Doors, Inc.'));
        chunks.push(String(b.content.address ?? 'City of Industry, CA'));
        chunks.push('Unsubscribe: {{unsubscribe_url}}');
        break;
    }
  }
  return chunks.join('\n').trim();
}
